import { Label } from "@/components/ui/label";
import { CheckCircle2, Clock, AlertTriangle, XCircle } from "lucide-react";

export function Th({ children }) {
  return <th className="text-left px-4 py-3 font-semibold">{children}</th>;
}

export function Td({ children, className = "" }) {
  return <td className={`px-4 py-4 ${className}`}>{children}</td>;
}

export function Stat({ label, value, accent }) {
  const color =
    accent === "warning" ? "text-[#9b6a26]" :
    accent === "error" ? "text-[#B24C4C]" :
    "text-gg-green";
  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface p-5">
      <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">{label}</p>
      <p className={`font-heading text-4xl mt-2 leading-none ${color}`}>{value}</p>
    </div>
  );
}

export function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
        active
          ? "border-gg-green text-gg-green"
          : "border-transparent text-gg-muted hover:text-gg-brown"
      }`}
    >
      {children}
    </button>
  );
}

export function FieldLabel({ label, children, className = "" }) {
  return (
    <Label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">{label}</span>
      <div className="mt-1.5">{children}</div>
    </Label>
  );
}

export function ToggleChip({ checked, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
        checked
          ? "bg-gg-green text-white"
          : "bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green"
      }`}
    >
      {label}
    </button>
  );
}

export function StatusBadge({ status }) {
  const map = {
    requested: { c: "bg-[#D9933B]/15 text-[#9b6a26]", l: "Requested", I: Clock },
    confirmed: { c: "bg-gg-green/15 text-gg-green", l: "Confirmed", I: CheckCircle2 },
    declined: { c: "bg-[#B24C4C]/15 text-[#B24C4C]", l: "Declined", I: XCircle },
    expired: { c: "bg-gg-muted/15 text-gg-muted", l: "Expired", I: AlertTriangle },
  };
  const { c, l, I } = map[status] || map.requested;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${c}`}>
      <I size={12} /> {l}
    </span>
  );
}

export function CwStatusBadge({ reg }) {
  // Highest priority: registration is fully paid (direct-pay flow or manually marked paid).
  if (reg.payment_method === "direct" && reg.parent_payment_status === "paid") {
    const label = reg.manual_payment_reference ? "Paid (Manual)" : "Paid (Stripe)";
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gg-green/15 text-gg-green text-xs font-semibold"
        title={reg.manual_payment_reference || (reg.stripe_paid_at ? `Stripe — ${reg.stripe_paid_at}` : "")}
        data-testid={`cw-paid-badge-${reg.id}`}
      >
        <CheckCircle2 size={12} /> {label}
      </span>
    );
  }
  if (reg.classwallet_status === "submitted") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gg-green/15 text-gg-green text-xs font-semibold">
        <CheckCircle2 size={12} /> Submitted
      </span>
    );
  }
  if (reg.classwallet_expired || reg.classwallet_status === "expired") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#B24C4C]/15 text-[#B24C4C] text-xs font-semibold">
        <AlertTriangle size={12} /> Expired
      </span>
    );
  }
  const days = reg.classwallet_days_remaining ?? 7;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D9933B]/15 text-[#9b6a26] text-xs font-semibold">
      <Clock size={12} /> {days}d left
    </span>
  );
}
